var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faSparkles } from '@fortawesome/pro-duotone-svg-icons';
import { faArrowUpRightFromSquare, faPaperPlaneTop, faRotateLeft, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Chip, CircularProgress, Fab, IconButton, Paper, TextField, Tooltip, Typography, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAPI } from '../../../state/api';
import { Markdown } from './markdown';
import { useAssistantChat } from './useAssistantChat';
export var ASSISTANT_API = '/api/dashboards/assistant';
var EXAMPLES = ['explain', 'fte', 'build'];
/** The tools the assistant runs (packages/superset/cinareo_mcp.py), each shown as progress. */
var STEPS = ['list_dashboards', 'get_dashboard_info', 'list_charts', 'get_chart_info', 'get_chart_data', 'list_datasets', 'get_dataset_info', 'get_chart_type_schema', 'generate_explore_link', 'generate_chart', 'update_chart', 'update_chart_preview', 'generate_dashboard', 'add_chart_to_existing_dashboard'];
var isStep = function (tool) {
  return STEPS.includes(tool);
};
var Panel = styled(Paper)(function (_a) {
  var theme = _a.theme;
  return {
    position: 'absolute',
    right: theme.spacing(4),
    bottom: theme.spacing(20),
    width: 'min(420px, calc(100% - 2rem))',
    height: 'min(640px, calc(100% - 6rem))',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 2
  };
});
var PanelHeader = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(3, 4),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  };
});
var Messages = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(4)
  };
});
var Bubble = styled(Box, {
  shouldForwardProp: function (prop) {
    return prop !== 'mine';
  }
})(function (_a) {
  var theme = _a.theme,
    mine = _a.mine;
  return {
    alignSelf: mine ? 'flex-end' : 'flex-start',
    maxWidth: '90%',
    padding: theme.spacing(2, 3),
    borderRadius: 8,
    backgroundColor: mine ? theme.palette.primary.main : theme.palette.grey[100],
    color: mine ? theme.palette.common.white : theme.palette.text.primary,
    whiteSpace: mine ? 'pre-wrap' : undefined,
    overflowWrap: 'anywhere'
  };
});
var Launcher = styled(Fab)(function (_a) {
  var theme = _a.theme;
  return {
    position: 'absolute',
    right: theme.spacing(4),
    bottom: theme.spacing(4),
    zIndex: 2
  };
});
/** A question as asked, an answer as markdown, or why there is no answer. */
var EntryContent = function (_a) {
  var entry = _a.entry;
  var t = useTranslation().t;
  if (entry.role === 'user') {
    return _jsx(Typography, __assign({
      variant: "body2"
    }, {
      children: entry.content
    }));
  }
  if (entry.error) {
    return _jsx(Typography, __assign({
      variant: "body2",
      color: "error"
    }, {
      children: t("dashboards.assistant.errors.".concat(entry.error))
    }));
  }
  return _jsx(Markdown, {
    text: entry.content
  });
};
/**
 * The dashboards assistant: a button in the bottom-right of the dashboards that opens a chat. It
 * answers questions about the dashboards and their data and builds charts and dashboards, as the
 * user (api src/dashboards/assistant). Shown only where the api offers it.
 */
export var DashboardAssistant = function (_a) {
  var view = _a.view,
    path = _a.path,
    onOpen = _a.onOpen;
  var t = useTranslation().t;
  var _b = useAPI(ASSISTANT_API),
    status = _b[0].data,
    fetchStatus = _b[1];
  var _c = useState(false),
    open = _c[0],
    setOpen = _c[1];
  var _d = useState(''),
    draft = _d[0],
    setDraft = _d[1];
  var _e = useAssistantChat({
      view: view,
      path: path
    }),
    entries = _e.entries,
    pending = _e.pending,
    step = _e.step,
    send = _e.send,
    reset = _e.reset;
  var endRef = useRef(null);
  useEffect(function () {
    fetchStatus();
  }, [fetchStatus]);
  useEffect(function () {
    var _a, _b;
    (_b = (_a = endRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, {
      block: 'end'
    });
  }, [entries, pending, open]);
  if (!(status === null || status === void 0 ? void 0 : status.enabled)) {
    return null;
  }
  var ask = function (question) {
    var trimmed = question.trim();
    if (trimmed && !pending) {
      setDraft('');
      send(trimmed);
    }
  };
  var handleKeyDown = function (event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      ask(draft);
    }
  };
  return _jsxs(_Fragment, {
    children: [open && _jsxs(Panel, __assign({
      elevation: 8,
      role: "dialog",
      "aria-label": t('dashboards.assistant.title')
    }, {
      children: [_jsxs(PanelHeader, {
        children: [_jsx(FontAwesomeIcon, {
          icon: faSparkles
        }), _jsx(Typography, __assign({
          variant: "subtitle1",
          sx: {
            flex: 1,
            fontWeight: 'bold'
          }
        }, {
          children: t('dashboards.assistant.title')
        })), _jsx(Tooltip, __assign({
          title: t('dashboards.assistant.clear')
        }, {
          children: _jsx("span", {
            children: _jsx(IconButton, __assign({
              "aria-label": t('dashboards.assistant.clear'),
              onClick: reset,
              disabled: entries.length === 0 && !pending,
              sx: {
                color: 'inherit'
              },
              size: "small"
            }, {
              children: _jsx(FontAwesomeIcon, {
                icon: faRotateLeft
              })
            }))
          })
        })), _jsx(IconButton, __assign({
          "aria-label": t('dashboards.assistant.close'),
          onClick: function () {
            return setOpen(false);
          },
          sx: {
            color: 'inherit'
          },
          size: "small"
        }, {
          children: _jsx(FontAwesomeIcon, {
            icon: faXmark
          })
        }))]
      }), _jsxs(Messages, __assign({
        "aria-live": "polite"
      }, {
        children: [entries.length === 0 && _jsxs(Box, __assign({
          sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'flex-start'
          }
        }, {
          children: [_jsx(Typography, __assign({
            variant: "body2",
            color: "text.secondary"
          }, {
            children: t('dashboards.assistant.subtitle')
          })), EXAMPLES.map(function (example) {
            return _jsx(Chip, {
              label: t("dashboards.assistant.examples.".concat(example)),
              variant: "outlined",
              onClick: function () {
                return ask(t("dashboards.assistant.examples.".concat(example)));
              }
            }, example);
          })]
        })), entries.map(function (entry) {
          var _a;
          return _jsxs(Bubble, __assign({
            mine: entry.role === 'user'
          }, {
            children: [_jsx(EntryContent, {
              entry: entry
            }), (_a = entry.links) === null || _a === void 0 ? void 0 : _a.map(function (link) {
              return _jsxs(Button, __assign({
                size: "small",
                variant: "outlined",
                startIcon: _jsx(FontAwesomeIcon, {
                  icon: faArrowUpRightFromSquare
                }),
                onClick: function () {
                  return onOpen(link.path);
                },
                sx: {
                  marginTop: 2,
                  marginRight: 2,
                  bgcolor: 'common.white'
                }
              }, {
                children: [t(link.kind === 'dashboard' ? 'dashboards.assistant.openDashboard' : 'dashboards.assistant.openChart'), link.title && ": ".concat(link.title)]
              }), link.path);
            })]
          }), entry.id);
        }), pending && _jsxs(Box, __assign({
          sx: {
            display: 'flex',
            alignItems: 'center',
            gap: 2
          },
          role: "status"
        }, {
          children: [_jsx(CircularProgress, {
            size: 16
          }), _jsx(Typography, __assign({
            variant: "body2",
            color: "text.secondary"
          }, {
            children: isStep(step) ? t("dashboards.assistant.steps.".concat(step)) : t('dashboards.assistant.working')
          }))]
        })), _jsx("div", {
          ref: endRef
        })]
      })), _jsxs(Box, __assign({
        sx: {
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          padding: 3,
          borderTop: 1,
          borderColor: 'divider'
        }
      }, {
        children: [_jsx(TextField, {
          value: draft,
          onChange: function (event) {
            return setDraft(event.target.value);
          },
          onKeyDown: handleKeyDown,
          placeholder: t('dashboards.assistant.placeholder'),
          inputProps: {
            'aria-label': t('dashboards.assistant.placeholder'),
            maxLength: 4000
          },
          multiline: true,
          maxRows: 5,
          size: "small",
          fullWidth: true,
          autoFocus: true
        }), _jsx(IconButton, __assign({
          "aria-label": t('dashboards.assistant.send'),
          color: "primary",
          onClick: function () {
            return ask(draft);
          },
          disabled: pending || !draft.trim()
        }, {
          children: _jsx(FontAwesomeIcon, {
            icon: faPaperPlaneTop
          })
        }))]
      })), _jsx(Typography, __assign({
        variant: "caption",
        color: "text.secondary",
        sx: {
          padding: '0 0.75rem 0.5rem'
        }
      }, {
        children: t('dashboards.assistant.disclaimer')
      }))]
    })), _jsx(Tooltip, __assign({
      title: t('dashboards.assistant.open'),
      placement: "left"
    }, {
      children: _jsx(Launcher, __assign({
        color: "primary",
        "aria-label": t('dashboards.assistant.open'),
        onClick: function () {
          return setOpen(function (isOpen) {
            return !isOpen;
          });
        }
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faSparkles,
          size: "lg"
        })
      }))
    }))]
  });
};